import { feedItemId } from '@/lib/google-merchant-feed';

/**
 * Google Analytics 4 ecommerce helpers.
 *
 * These run alongside PostHog (see `src/lib/analytics.ts`). Their purpose is to
 * feed Google Merchant Center / Google Ads via a linked GA4 property. The
 * critical event is `purchase`; `view_item`, `add_to_cart` and `begin_checkout`
 * complete the funnel for GA4 reporting and remarketing.
 *
 * `item_id` MUST equal the Merchant Center feed `g:id` (see `feedItemId`) so
 * Google can attribute purchases to the correct product.
 */

export const GA4_MEASUREMENT_ID =
  process.env.NEXT_PUBLIC_GA4_ID || 'G-95N1RZP24V';

/**
 * Google Ads (gtag) account id. Used both for the global `config` in the root
 * layout (remarketing/audiences) and as the prefix of conversion `send_to`
 * targets below.
 */
export const GOOGLE_ADS_ID =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_ID || 'AW-18224298801';

/**
 * Conversion label for the "Purchase" conversion action, e.g. `AbC-D_efGh12`.
 * Get it from Google Ads → Goals → Conversions → (your purchase action) →
 * "Tag setup" → "Use Google tag" → the value after the slash in
 * `send_to: 'AW-XXXX/LABEL'`. Leave empty to disable the Ads purchase
 * conversion (GA4 `purchase` still fires).
 */
export const GOOGLE_ADS_PURCHASE_LABEL =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_PURCHASE_LABEL || '';

type GtagArgs =
  | ['js', Date]
  | ['config', string, Record<string, unknown>?]
  | ['event', string, Record<string, unknown>?]
  | ['consent', 'default' | 'update', Record<string, unknown>]
  | ['set', Record<string, unknown>]
  | ['set', string, unknown]
  | ['get', string, string, (value: unknown) => void];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: GtagArgs) => void;
  }
}

export interface GaItemInput {
  /** Product id/slug. Combined with `size` to form the Merchant Center item id. */
  slug: string;
  size: string;
  name: string;
  /** Unit price in major units (e.g. kroner), not øre/cents. */
  price: number;
  quantity?: number;
}

interface GaItem {
  item_id: string;
  item_name: string;
  item_variant: string;
  price: number;
  quantity: number;
}

function toGaItems(items: GaItemInput[]): GaItem[] {
  return items.map((item) => ({
    item_id: feedItemId(item.slug, item.size),
    item_name: item.name,
    item_variant: item.size,
    price: Number(item.price.toFixed(2)),
    quantity: item.quantity ?? 1,
  }));
}

function sumValue(items: GaItem[]): number {
  return Number(
    items.reduce((total, item) => total + item.price * item.quantity, 0).toFixed(2)
  );
}

function emit(event: string, params: Record<string, unknown>): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', event, params);
}

/** localStorage key holding the visitor's consent choice (`granted` | `denied`). */
export const CONSENT_STORAGE_KEY = 'klonkaffe-consent';

export type ConsentChoice = 'granted' | 'denied';

/** Read the stored consent choice, or `null` if the visitor hasn't decided yet. */
export function getStoredConsent(): ConsentChoice | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = localStorage.getItem(CONSENT_STORAGE_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    return null;
  }
}

/**
 * Apply (and persist) the visitor's consent choice. Updates Google Consent
 * Mode v2 ad/analytics signals and toggles the Meta Pixel — granting enables
 * personalised ads + remarketing; denying keeps everything cookieless.
 */
export function applyConsent(choice: ConsentChoice): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch {
    /* storage may be unavailable (private mode) — consent still applies for this session */
  }

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      ad_storage: choice,
      ad_user_data: choice,
      ad_personalization: choice,
      analytics_storage: choice,
    });
    window.gtag('set', 'ads_data_redaction', choice !== 'granted');
  }

  const fbq = (window as unknown as { fbq?: (...args: unknown[]) => void }).fbq;
  if (typeof fbq === 'function') {
    fbq('consent', choice === 'granted' ? 'grant' : 'revoke');
  }
}

export function gaViewItem(item: GaItemInput, currency: string): void {
  const items = toGaItems([item]);
  emit('view_item', {
    currency: currency.toUpperCase(),
    value: sumValue(items),
    items,
  });
}

export function gaAddToCart(item: GaItemInput, currency: string): void {
  const items = toGaItems([item]);
  emit('add_to_cart', {
    currency: currency.toUpperCase(),
    value: sumValue(items),
    items,
  });
}

export function gaBeginCheckout(items: GaItemInput[], currency: string): void {
  const gaItems = toGaItems(items);
  emit('begin_checkout', {
    currency: currency.toUpperCase(),
    value: sumValue(gaItems),
    items: gaItems,
  });
}

export interface GaPurchaseInput {
  transactionId: string;
  currency: string;
  /** Total order value in major units, including shipping if available. */
  value: number;
  shipping?: number;
  items: GaItemInput[];
}

/**
 * Read the GA4 `client_id` (from the `_ga` cookie via gtag). Passed through
 * Stripe metadata so the server-side Measurement Protocol `purchase` event can
 * be attributed to the same user/session as the client-side events.
 */
export function getGaClientId(): Promise<string | null> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
      resolve(null);
      return;
    }
    let settled = false;
    const done = (value: string | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    // gtag's get callback occasionally never fires (e.g. blocked); guard with a timeout.
    setTimeout(() => done(null), 800);
    window.gtag('get', GA4_MEASUREMENT_ID, 'client_id', (id: unknown) => {
      done(typeof id === 'string' ? id : null);
    });
  });
}

export function getGaSessionId(): Promise<string | null> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
      resolve(null);
      return;
    }
    let settled = false;
    const done = (value: string | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    setTimeout(() => done(null), 800);
    window.gtag('get', GA4_MEASUREMENT_ID, 'session_id', (id: unknown) => {
      done(id != null ? String(id) : null);
    });
  });
}

export function gaPurchase(order: GaPurchaseInput): void {
  const items = toGaItems(order.items);
  emit('purchase', {
    transaction_id: order.transactionId,
    currency: order.currency.toUpperCase(),
    value: Number(order.value.toFixed(2)),
    ...(typeof order.shipping === 'number'
      ? { shipping: Number(order.shipping.toFixed(2)) }
      : {}),
    items,
  });
}

async function sha256Hex(input: string): Promise<string | null> {
  if (typeof window === 'undefined' || !window.crypto?.subtle) return null;
  try {
    const data = new TextEncoder().encode(input);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}

/**
 * Provide first-party data for Google Ads **enhanced conversions**. We hash the
 * email with SHA-256 in the browser (Google never receives the raw address) and
 * hand it to gtag via `user_data`. gtag attaches it to the next conversion in
 * the same context, improving match rates for attribution lost to cookie/ITP
 * limits.
 *
 * Must be called and awaited *before* `gaAdsPurchaseConversion` so the data is
 * associated with the purchase. Requires "Enhanced conversions" to be turned on
 * for the conversion action in Google Ads. No-ops without a usable email.
 */
export async function gaSetEnhancedConversionData(email: string): Promise<void> {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const normalized = email.trim().toLowerCase();
  if (!normalized) return;
  const hashed = await sha256Hex(normalized);
  if (!hashed) return;
  window.gtag('set', 'user_data', { sha256_email_address: hashed });
}

/**
 * Fire the Google Ads "Purchase" conversion. This is separate from the GA4
 * `purchase` event above: GA4 feeds reporting/Merchant Center, while this
 * reports a billable conversion to the Ads account so campaigns can optimise
 * for and report revenue.
 *
 * `transaction_id` lets Google Ads de-duplicate if the success page is
 * reloaded — the same order is only counted once. No-ops if no conversion
 * label is configured.
 */
export function gaAdsPurchaseConversion(order: GaPurchaseInput): void {
  if (!GOOGLE_ADS_PURCHASE_LABEL) return;
  emit('conversion', {
    send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_PURCHASE_LABEL}`,
    transaction_id: order.transactionId,
    currency: order.currency.toUpperCase(),
    value: Number(order.value.toFixed(2)),
  });
}
